import { Badge } from 'features/catalog/useBadges';
import { BADGE_STAT_KEYS, type BadgeStatKey } from 'features/catalog/badge-definitions';

type Wash = [string, string, string, string];
type WashTone = { soft: string; mid: string; peak: string };

/** Soft multi-tone washes so mono-rarity highlights still feel dimensional. */
const RARITY_WASH: Record<string, Wash> = {
  mythic: ['#f3e8ff', '#c084fc', '#853ec7', '#5b21b6'],
  // Cool champagne gold — punched ~25% richer (still icy, not amber).
  legendary: ['#fff4d6', '#ffd45c', '#efb820', '#d49a12'],
  rare: ['#e0f2fe', '#38bdf8', '#0696ef', '#0369a1'],
  common: ['#f1f5f1', '#c5cbc5', '#8b918c', '#6b716d'],
};

/**
 * Deck identity colors — jewel mids/deeps so a wash still reads as that stat.
 * Each stat also has an alt family so two culture-lead decks can land in
 * different neighborhoods (terracotta vs wine, indigo vs cyan, …).
 */
export const BADGE_STAT_WASH: Record<BadgeStatKey, Wash> = {
  culture: ['#f6c9b0', '#e07a52', '#c4452a', '#7a2218'],
  engineering: ['#c5d4ff', '#4d6fff', '#2c3fd0', '#121a6a'],
  growth: ['#c6ebc0', '#3db85a', '#1a7a3a', '#0c3d20'],
  fun: ['#ffc2d4', '#ff2d6f', '#d10f52', '#8a0a38'],
  spotlight: ['#ffe9a0', '#f0b429', '#d4890a', '#7a4a08'],
};

export const BADGE_STAT_ALT: Record<BadgeStatKey, Wash> = {
  culture: ['#f5d0c8', '#d45a6a', '#a83248', '#6b1c30'],
  engineering: ['#b8f0ff', '#2eb8e8', '#0e7aa8', '#0a3d58'],
  growth: ['#d4f0c8', '#8fbc3a', '#5a7a18', '#2a480e'],
  fun: ['#ffd0b8', '#ff6a3d', '#d44518', '#8a280c'],
  spotlight: ['#ffe3c4', '#e0893c', '#b85c18', '#6b340c'],
};

export const statWashHexes = (key: BadgeStatKey): string[] => [...BADGE_STAT_WASH[key], ...BADGE_STAT_ALT[key]];

export type HashWashSurface = 'light' | 'dark';

const rarityKey = (name: string): keyof typeof RARITY_WASH =>
  name in RARITY_WASH ? (name as keyof typeof RARITY_WASH) : 'common';

/**
 * Highlights is a short wide band. Near-horizontal angles read as plain L→R;
 * ~65deg keeps a clear BL→TR tilt (roughly mid-low on the left → mid-high on the right).
 */
const HIGHLIGHTS_GRADIENT_ANGLE = '65deg';

/** Full-deck group fill — same as the previous static Your Deck wash. */
export const DECK_WASH_MAX_OPACITY = 0.55;
/** Unselected saved-deck wash only — buttons and type stay full strength. */
export const SAVED_DECK_WASH_OPACITY = 0.2;
export const SAVED_DECK_WASH_OPACITY_LOADED = 0.6;

export const deckWashOpacity = (filled: number, size: number): number =>
  size <= 0 || filled <= 0 ? 0 : Math.min(1, filled / size) * DECK_WASH_MAX_OPACITY;

const toneFromWash = (wash: Wash, surface: HashWashSurface, punchDeep?: boolean): WashTone => {
  const [, soft, mid, deep] = wash;
  const peak = punchDeep ? `color-mix(in oklab, ${mid} 45%, ${deep})` : `color-mix(in oklab, ${mid} 70%, ${deep})`;
  if (surface === 'light') {
    if (punchDeep) {
      return {
        soft: `color-mix(in oklab, ${soft} 20%, ${mid})`,
        mid: `color-mix(in oklab, ${mid} 70%, ${deep})`,
        peak: deep,
      };
    }
    return {
      soft: `color-mix(in oklab, ${soft} 35%, ${mid})`,
      mid: `color-mix(in oklab, ${mid} 85%, ${deep})`,
      peak,
    };
  }
  if (punchDeep) {
    return {
      soft: `color-mix(in oklab, ${soft} 40%, ${mid})`,
      mid,
      peak,
    };
  }
  return { soft, mid, peak };
};

/** Same soft / mid / peak for mono hashes and multi-hash segments of that rarity. */
const washTone = (rarity: string, surface: HashWashSurface): WashTone => {
  const key = rarityKey(rarity);
  return toneFromWash(RARITY_WASH[key], surface, key === 'legendary');
};

const gradientFromTones = (tones: WashTone[], weights: number[], angle: string): string => {
  if (tones.length === 1) {
    const { soft, mid, peak } = tones[0];
    return `linear-gradient(in oklab ${angle}, ${soft} 0%, ${mid} 28%, ${peak} 52%, ${mid} 78%, ${soft} 100%)`;
  }

  let cursor = 0;
  const stops: string[] = [];
  for (let i = 0; i < tones.length; i++) {
    const { soft, mid, peak } = tones[i];
    const midPt = cursor + weights[i] / 2;
    if (i === 0) {
      stops.push(`${soft} 0%`, `${peak} ${midPt}%`);
    } else if (i === tones.length - 1) {
      stops.push(`${mid} ${midPt}%`, `${soft} 100%`);
    } else {
      stops.push(`${peak} ${midPt}%`);
    }
    cursor += weights[i];
  }

  return `linear-gradient(in oklab ${angle}, ${stops.join(', ')})`;
};

const monoRarityGradient = (rarity: string, surface: HashWashSurface, angle: string): string =>
  gradientFromTones([washTone(rarity, surface)], [100], angle);

export type HashGradientOptions = {
  /** `light` = richer jewel tones for the white badge modal. Default `dark`. */
  surface?: HashWashSurface;
  angle?: string;
};

/** Mono rarity wash — same look as a Badge Hash of that rarity ×5. */
export const gradientFromRarity = (rarityName: string, options?: HashGradientOptions): string =>
  monoRarityGradient(rarityName, options?.surface ?? 'dark', options?.angle ?? HIGHLIGHTS_GRADIENT_ANGLE);

/**
 * Badge Hash segment weights (must sum to 100):
 * 25% → 25% → 20% → 20% → 10% (rarest first).
 */
export const HASH_SEGMENT_WEIGHTS = [25, 25, 20, 20, 10] as const;

const segmentWeightsFor = (count: number): number[] => {
  if (count === HASH_SEGMENT_WEIGHTS.length) return [...HASH_SEGMENT_WEIGHTS];
  if (count <= 0) return [];
  const even = 100 / count;
  return Array.from({ length: count }, () => even);
};

/** Badge Hash–style wash from rarity names (rarest first). */
export const gradientFromRarities = (rarities: readonly string[], options?: HashGradientOptions): string => {
  const surface = options?.surface ?? 'dark';
  const angle = options?.angle ?? HIGHLIGHTS_GRADIENT_ANGLE;
  const keys = rarities.map(rarityKey);

  if (keys.length === 0) return monoRarityGradient('common', surface, angle);

  const unique = [...new Set(keys)];
  if (unique.length === 1) return monoRarityGradient(unique[0], surface, angle);

  const weights = segmentWeightsFor(keys.length);
  return gradientFromTones(
    keys.map((key) => washTone(key, surface)),
    weights,
    angle
  );
};

/** Badge Hash–style wash from a set of badges (rarity display order, actual rarities). */
export const gradientFromHighlightBadges = (badges: Badge[], options?: HashGradientOptions): string =>
  gradientFromRarities(
    badges.map((badge) => badge.rarityName),
    options
  );

const STAT_PRIME: Record<BadgeStatKey, number> = {
  culture: 3,
  engineering: 7,
  growth: 11,
  fun: 13,
  spotlight: 17,
};

const rankedDeckStats = (totals: Record<BadgeStatKey, number>): { key: BadgeStatKey; value: number }[] =>
  BADGE_STAT_KEYS.map((key) => ({ key, value: totals[key] ?? 0 }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value || BADGE_STAT_KEYS.indexOf(a.key) - BADGE_STAT_KEYS.indexOf(b.key));

const statSignature = (ranked: { key: BadgeStatKey; value: number }[]): number =>
  ranked.reduce((sum, entry) => sum + entry.value * STAT_PRIME[entry.key], 0);

const washFor = (key: BadgeStatKey, value: number, salt: number): Wash =>
  (value + STAT_PRIME[key] + salt) % 2 === 0 ? BADGE_STAT_WASH[key] : BADGE_STAT_ALT[key];

const hueNudge = (color: string, degrees: number): string => {
  const shift = Math.round(degrees);
  if (shift === 0) return color;
  return `oklch(from ${color} l c calc(h + ${shift}))`;
};

/**
 * Smooth two-pole wash from a deck's leading stats. Weaker stats rotate those
 * hues instead of averaging into mud, so nearby decks keep distinct colors.
 */
export const gradientFromDeckStats = (
  totals: Record<BadgeStatKey, number>,
  options?: HashGradientOptions
): string | null => {
  const ranked = rankedDeckStats(totals);
  if (ranked.length === 0) return null;

  const signature = statSignature(ranked);
  const angle = options?.angle ?? `${48 + ((signature * 5) % 89)}deg`;
  const lead = ranked[0];
  const leadWash = washFor(lead.key, lead.value, signature);

  if (ranked.length === 1) {
    const shift = ((lead.value * STAT_PRIME[lead.key]) % 25) - 12;
    const soft = hueNudge(leadWash[1], shift - 4);
    const mid = hueNudge(leadWash[2], shift);
    const deep = hueNudge(leadWash[3], shift + 6);
    const peakAt = 46 + ((lead.value * STAT_PRIME[lead.key]) % 23);
    return `linear-gradient(in oklab ${angle}, ${soft} 0%, ${mid} ${Math.max(18, peakAt - 22)}%, ${deep} ${peakAt}%, ${mid} ${Math.min(88, peakAt + 22)}%, ${soft} 100%)`;
  }

  const second = ranked[1];
  const third = ranked[2];
  const secondWash = washFor(second.key, second.value, signature * 3);
  const thirdShift = third ? third.value + STAT_PRIME[third.key] : 0;
  const start = hueNudge(leadWash[2], (signature % 29) - 14 + thirdShift);
  const endPole = hueNudge(secondWash[3], ((signature * 7) % 33) - 16 - Math.round(thirdShift * 0.6));
  const end = third ? `color-mix(in oklab, ${endPole} 78%, ${washFor(third.key, third.value, signature)[2]})` : endPole;
  const pairTotal = lead.value + second.value;
  const leadShare = pairTotal <= 0 ? 0.5 : lead.value / pairTotal;
  const accentAt = Math.min(70, Math.max(16, Math.round(22 + leadShare * 38 + (signature % 9) - 4)));
  const hold = Math.max(0, Math.round(accentAt * 0.3 + (signature % 7) - 3));
  return `linear-gradient(in oklab ${angle}, ${start} 0%, ${start} ${hold}%, ${end} ${accentAt}%, ${end} 100%)`;
};
